import React, { ReactNode } from 'react';
import { useFormik } from 'formik';
import _, { debounce } from 'lodash';

import {
  Row,
  Col,
  Button,
  Modal,
  Form,
} from 'react-bootstrap';
import styled from 'styled-components';

import { Spin, Switch } from 'antd';

import Select from '../../../components/select/Select.tsx';

// Redux
import { useDispatch } from 'react-redux';
import { RootState, AppDispatch, useTypedSelector } from '../../../utils/store.tsx';
import slice, { fields, FormikContext, initialValues, formSchema, prepareForm, prepareData } from './slice.tsx';

import { BillingTypes } from '../../jobs/slice';

// enums
import { isNumeric } from '../../../utils/utils.tsx';
// import {  } from '../../../utils/enums.tsx';

// import { ReactComponent as InfoIcon } from "../../../assets/icons/alert-info.svg";

// ----------------------------------------------------------------------

const StyledModal = styled(Modal)`
  .modal-dialog {
    min-width: 325px;
  }
`;


const FormElement = ({ onCreate, onUpdate, onDelete }: any) => {
  const { isLoading, show, index, id, details } = useTypedSelector((state: RootState) => state.servicesBillingCategorySlice);
  const dispatch = useDispatch<AppDispatch>();


  React.useEffect(() => {
    if(show && (id && id > 0)){
      dispatch(slice.callDetailsApi(id, (state: boolean, data: any) => {}));
    }
  }, [id]);

  React.useEffect(() => {
    dispatch(slice.setLoading(true));
    let form = prepareForm(details, initialValues);
    setValues(form);
    dispatch(slice.setLoading(false));
  }, [details]);


  const debouncedCheckName = React.useCallback(
    debounce((params: any) => {
      dispatch(slice.callChargeCategoryCheckNameApi(params, (state: boolean, data: any) => {}));
    }, 500),
    [dispatch]
  );
  React.useEffect(() => {
    return () => {
      debouncedCheckName.cancel();
    };
  }, [debouncedCheckName]);


  const formik = useFormik({
    initialValues: initialValues,
    validateOnMount: false,
    validateOnChange: false,
    validationSchema: formSchema(null),
    onSubmit: values => {
      onSubmit(values, (data: any) => {
        onCancel();
      });
    },
  });
  const { values, errors, setValues, setFieldValue, validateForm, handleSubmit }: any = formik;


  const onSubmit = (values: any, callback: (data: any) => void) => {
    let data = prepareData(values, id);
    if(id && id > 0){
      dispatch(slice.callUpdateApi(data, (state: boolean, data: any) => {
        if(state){
          if(onUpdate){
            onUpdate(index, data);
          }
          
          callback(data);
        }
      }));
    } else {
      dispatch(slice.callCreateApi(data, (state: boolean, data: any) => {
        if(state){
          if(onCreate){
            onCreate(index, data);
          }

          callback(data);
        }
      }));
    }
  }
  const onCancel = () => {
    let form = prepareForm(null, initialValues);
    setValues(form);
    dispatch(slice.setValues(form));
    dispatch(slice.setShow({ show: false, index: null, id: null }));
    dispatch(slice.resetSlice());
  }


  const setFooter = () => {
    return <Row className='gx-12 w-100'>
      <Col xs={true}>
        {(id && id > 0) && <Button
          variant={'custom-danger'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            dispatch(slice.callDeleteApi({ defaultLocationId: id }, (state: boolean, data: any) => {
              if(state){
                if(onDelete){
                  onDelete(index, id);
                }

                onCancel();
              }
            }));
          }}
        >Delete</Button>}
      </Col>
      <Col xs={'auto'}>
        <Button
          variant={'custom-text'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            onCancel();
          }}
        >Cancel</Button>
      </Col>
      <Col xs={'auto'}>
        <Button
          variant={'custom-primary'}
          size={'sm'}
          disabled={isLoading}
          onClick={() => {
            handleSubmit();
          }}
        >Save</Button>
      </Col>
    </Row>
  }

  const setForm = () => {
    return <Row className={'g-16'}>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{fields.chargeCategoryName.label}</Form.Label>
          <Form.Control
            type={'text'}
            autoFocus={true}
            autoComplete='off'
            placeholder={fields.chargeCategoryName.placeholder}
            disabled={false}
            value={values.chargeCategoryName}
            onChange={(e: any) => {
              setFieldValue('chargeCategoryName', e.target.value);
              
              let params = {
                chargeCategoryId: isNumeric(values.chargeCategoryId) ? values.chargeCategoryId : 0,
                chargeCategoryName: e.target.value,
              };
              debouncedCheckName(params);
            }}
            isInvalid={!!(errors && errors.chargeCategoryName)}
          />
          <Form.Control.Feedback type="invalid">{errors && errors.chargeCategoryName as ReactNode}</Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{fields.billingType.label}</Form.Label>
          <Form.Control
            as={Select}
            value={values.billingType}
            placeholder={fields.billingType.placeholder}
            searchable={false}
            allowClear={false}
            selectedIcon={true}
            loading={false}
            disabled={false}
            getPopupContainer={(trigger: any) => trigger.parentNode}
            onChangeValue={(value: any, option: any, ref: any) => {
              setFieldValue('billingType', value);

              if(value == BillingTypes.Expense){
                setFieldValue('useBinWeightQuantity', false);
              }
            }}
            options={[
              { value: BillingTypes.Invoice, label: 'Invoice' },
              { value: BillingTypes.Purchase, label: 'Purchase' },
              { value: BillingTypes.Expense, label: 'Expense' },
            ]}
            isInvalid={!!(errors && errors.billingType)}
          />
          <Form.Control.Feedback type="invalid">{errors && errors.billingType as ReactNode}</Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group>
          <Form.Label>{fields.useBinWeightQuantity.label}</Form.Label>
          <div>
          {
            (values.billingType === BillingTypes.Expense)
            ?
            <>{values.useBinWeightQuantity ? 'Yes' : 'No'}</>
            :
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              checked={values.useBinWeightQuantity}
              onChange={(checked: boolean) => {
                setFieldValue('useBinWeightQuantity', checked);
              }}
              disabled={false}
            />
          }
          </div>
          <Form.Control.Feedback type="invalid">{errors && errors.useBinWeightQuantity as ReactNode}</Form.Control.Feedback>
        </Form.Group>
      </Col>
    </Row>
  }


  return <StyledModal
    className='wp-v5'
    show={show}
    onHide={() => {
      onCancel();
    }}
    backdrop={'static'}
    keyboard={false}
    enforceFocus={true}
    centered={true}
    size={'sm'}
  >
    <FormikContext.Provider value={formik}>
      <Modal.Header closeButton>
        <h6 className='fw-medium'>{((id && id > 0) ? 'Edit' : 'Add')} Billing Category</h6>
      </Modal.Header>

      <Spin spinning={isLoading} wrapperClassName={'no-height'}>
        <Modal.Body className='pt-24'>{setForm()}</Modal.Body>
      </Spin>

      <Modal.Footer className='px-12'>{setFooter()}</Modal.Footer>
    </FormikContext.Provider>
  </StyledModal>
}

export default FormElement;
